import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';

interface CondizioniDealerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CondizioniDealerModal({ isOpen, onClose }: CondizioniDealerModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-full h-[90vh] p-0 gap-0 flex flex-col">
        <DialogHeader className="px-6 py-4 border-b bg-card flex-shrink-0">
          <DialogTitle className="text-lg font-semibold">Condizioni di Utilizzo per Operatori</DialogTitle>
        </DialogHeader>
        
        <div className="relative flex-1 bg-background min-h-0">
          <div className="p-6 overflow-y-auto h-full">
            <div className="prose prose-gray dark:prose-invert max-w-none">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-foreground mb-2">
                  Condizioni di Utilizzo per Operatori – Gildy
                </h2>
                <p className="text-sm text-muted-foreground">
                  Gildy – Area Dealer
                </p>
                <p className="text-sm text-muted-foreground">
                  Ultimo aggiornamento: 22 Febbraio 2026
                </p>
              </div>

              <div className="space-y-6">
                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">1. Titolare della Piattaforma</h3>
                  <p className="text-muted-foreground leading-relaxed mb-2">Gildy è un brand commerciale di proprietà di:</p>
                  <p className="text-muted-foreground leading-relaxed">
                    <strong className="text-foreground">Twibi S.r.l.</strong><br />
                    P.IVA IT15924631003<br />
                    Via Cola Di Rienzo 271, 00192 Roma (RM), Italia<br />
                    PEC: info@pec.twibi.it
                  </p>
                  <p className="text-muted-foreground leading-relaxed mt-2">
                    Email: <a href="mailto:info@gildy.it" className="text-gold hover:text-gold/80">info@gildy.it</a>
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">2. Oggetto</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Le presenti Condizioni disciplinano l'utilizzo della piattaforma Gildy da parte degli operatori professionali ("Operatore" o "Dealer") che intendono pubblicare la propria attività e utilizzare i servizi dedicati.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">3. Requisiti e Dichiarazioni dell'Operatore</h3>
                  <p className="text-muted-foreground leading-relaxed mb-2">L'Operatore dichiara:</p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>di essere regolarmente costituito e operativo;</li>
                    <li>di operare nel rispetto della normativa vigente;</li>
                    <li>di possedere eventuali registrazioni obbligatorie (OAM/OPO ove applicabile);</li>
                    <li>di fornire informazioni veritiere, aggiornate e complete.</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">4. Registrazione, Approvazione e Attivazione</h3>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>La registrazione avviene tramite procedura multi-step.</li>
                    <li>L'attivazione è soggetta a verifica manuale da parte del team Gildy.</li>
                    <li>La visibilità pubblica dipende da parametri tecnici interni e può essere sospesa o revocata in qualsiasi momento.</li>
                    <li>Twibi S.r.l. si riserva il diritto di rifiutare o revocare l'attivazione.</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">5. Servizi Offerti agli Operatori</h3>
                  <p className="text-muted-foreground leading-relaxed mb-2">La piattaforma consente all'Operatore di:</p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>configurare spread su metalli, monete, lingotti e diamanti;</li>
                    <li>impostare limiti minimi e massimi di transazione visibili agli utenti;</li>
                    <li>attivare il servizio Blocca Prezzo con durata configurabile (24/48/96 ore);</li>
                    <li>ricevere richieste utenti (blocchi prezzo e valutazioni);</li>
                    <li>gestire recensioni e risposte pubbliche;</li>
                    <li>accedere a dashboard statistiche e dati di performance;</li>
                    <li>caricare immagini, loghi e materiali informativi;</li>
                    <li>gestire orari di apertura;</li>
                    <li>inserire contatti e link social;</li>
                    <li>generare QR code della propria pagina pubblica;</li>
                    <li>gestire più sedi sotto lo stesso account (multi-sede).</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed mt-2">In caso di multi-sede, l'Operatore rimane unico responsabile di tutte le sedi associate al proprio account.</p>
                  <p className="text-muted-foreground leading-relaxed">Twibi S.r.l. può modificare o aggiornare funzionalità in qualsiasi momento.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">6. Profilo Pubblico e Indicizzazione</h3>
                  <p className="text-muted-foreground leading-relaxed mb-2">La scheda pubblica:</p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>è visibile anche a utenti non registrati;</li>
                    <li>può essere indicizzata dai motori di ricerca.</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed mt-2">I prezzi visualizzati sono calcolati automaticamente sulla base dello spread configurato dall'Operatore e delle quotazioni di mercato e hanno valore indicativo.</p>
                  <p className="text-muted-foreground leading-relaxed mt-2">Twibi S.r.l. può:</p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>ottimizzare campi SEO;</li>
                    <li>modificare URL pubblici per esigenze tecniche;</li>
                    <li>intervenire sui contenuti in caso di violazioni.</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">7. Servizi Dichiarati</h3>
                  <p className="text-muted-foreground leading-relaxed">I servizi selezionati dal catalogo devono essere effettivamente offerti.</p>
                  <p className="text-muted-foreground leading-relaxed">L'Operatore garantisce la veridicità delle informazioni fornite.</p>
                  <p className="text-muted-foreground leading-relaxed">Twibi S.r.l. può aggiornare o modificare il catalogo servizi.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">8. Sistema Crediti e Piano Flat</h3>
                  <p className="text-muted-foreground leading-relaxed mb-2">Alcune funzionalità richiedono l'utilizzo di crediti. I crediti:</p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>sono acquistati tramite Stripe;</li>
                    <li>non hanno scadenza;</li>
                    <li>non sono rimborsabili, anche in caso di sospensione o disattivazione dell'account;</li>
                    <li>non sono trasferibili;</li>
                    <li>non costituiscono valuta legale;</li>
                    <li>non garantiscono risultati commerciali.</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed mt-2">Il Piano Flat consente utilizzo illimitato del servizio Blocca Prezzo fino alla scadenza indicata.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">9. Servizio Blocca Prezzo e Valutazioni</h3>
                  <p className="text-muted-foreground leading-relaxed mb-2">La piattaforma consente:</p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>blocco prezzo metalli;</li>
                    <li>blocco prezzo prodotti (monete/lingotti);</li>
                    <li>richieste di valutazione diamanti.</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed mt-2">Ogni richiesta ricevuta comporta consumo di crediti, salvo Piano Flat attivo.</p>
                  <p className="text-muted-foreground leading-relaxed">La durata del blocco è configurabile dall'Operatore.</p>
                  <p className="text-muted-foreground leading-relaxed">Il QR code generato costituisce riferimento tecnico per la verifica in negozio.</p>
                  <p className="text-muted-foreground leading-relaxed">Twibi S.r.l. non è parte della trattativa tra utente e Operatore.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">10. Trasmissione e Trattamento Dati Utenti</h3>
                  <p className="text-muted-foreground leading-relaxed">I dati utente sono trasmessi esclusivamente al Dealer selezionato.</p>
                  <p className="text-muted-foreground leading-relaxed">L'Operatore agisce quale titolare autonomo del trattamento ai sensi del GDPR.</p>
                  <p className="text-muted-foreground leading-relaxed">Twibi S.r.l. non è responsabile del trattamento successivo effettuato dall'Operatore.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">11. Recensioni</h3>
                  <p className="text-muted-foreground leading-relaxed">Gli utenti possono pubblicare recensioni.</p>
                  <p className="text-muted-foreground leading-relaxed">Twibi S.r.l. può moderare o rimuovere contenuti non conformi.</p>
                  <p className="text-muted-foreground leading-relaxed">L'Operatore può rispondere pubblicamente.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">12. GTS, Ranking e Meccanismi Dinamici</h3>
                  <p className="text-muted-foreground leading-relaxed">Il Gildy Trust Score (GTS) è un sistema algoritmico proprietario.</p>
                  <p className="text-muted-foreground leading-relaxed">Può prevedere meccanismi di limitazione del punteggio, bonus temporanei e criteri dinamici di ranking.</p>
                  <p className="text-muted-foreground leading-relaxed">I parametri sono interni e modificabili in qualsiasi momento.</p>
                  <p className="text-muted-foreground leading-relaxed">Il punteggio non costituisce diritto acquisito.</p>
                  <p className="text-muted-foreground leading-relaxed">Twibi S.r.l. non è tenuta a rendere pubblici criteri o formule.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">13. Compliance e Badge</h3>
                  <p className="text-muted-foreground leading-relaxed">Il matching OAM/OPO può avvenire tramite verifica automatizzata.</p>
                  <p className="text-muted-foreground leading-relaxed">I badge possono essere revocati in caso di decadenza o incongruenze.</p>
                  <p className="text-muted-foreground leading-relaxed">I badge non costituiscono certificazione ufficiale.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">14. Proprietà Intellettuale e Contenuti Caricati</h3>
                  <h4 className="text-base font-semibold text-foreground mt-3 mb-2">14.1 Proprietà della Piattaforma</h4>
                  <p className="text-muted-foreground leading-relaxed">Il marchio "Gildy", il logo, il GTS, la struttura del database, l'algoritmo di ranking, il software, il codice, il design, l'interfaccia grafica, l'architettura del sito, gli URL pubblici generati e il sistema di QR code sono di proprietà esclusiva di Twibi S.r.l.</p>
                  <p className="text-muted-foreground leading-relaxed">All'Operatore è concessa licenza limitata, non esclusiva e non trasferibile per l'utilizzo dell'URL pubblico e del QR code per finalità promozionali connesse alla propria presenza sulla piattaforma.</p>
                  <p className="text-muted-foreground leading-relaxed">È vietato l'utilizzo del marchio Gildy senza autorizzazione scritta.</p>

                  <h4 className="text-base font-semibold text-foreground mt-3 mb-2">14.2 Contenuti Caricati dall'Operatore</h4>
                  <p className="text-muted-foreground leading-relaxed">I contenuti caricati (loghi, immagini, descrizioni, materiali informativi) restano di proprietà dell'Operatore o dei rispettivi titolari dei diritti.</p>
                  <p className="text-muted-foreground leading-relaxed">L'Operatore garantisce di essere titolare dei diritti o di avere le autorizzazioni necessarie.</p>

                  <h4 className="text-base font-semibold text-foreground mt-3 mb-2">14.3 Licenza Concessa a Twibi S.r.l.</h4>
                  <p className="text-muted-foreground leading-relaxed mb-2">Con il caricamento dei contenuti, l'Operatore concede a Twibi S.r.l. una licenza:</p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>non esclusiva;</li>
                    <li>gratuita;</li>
                    <li>mondiale;</li>
                    <li>trasferibile nei limiti tecnici necessari;</li>
                    <li>sublicenziabile per hosting, CDN e indicizzazione;</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed mt-2">per utilizzare, riprodurre, pubblicare e distribuire tali contenuti ai fini del funzionamento e della promozione della Piattaforma.</p>

                  <h4 className="text-base font-semibold text-foreground mt-3 mb-2">14.4 Rimozione dei Contenuti</h4>
                  <p className="text-muted-foreground leading-relaxed">Twibi S.r.l. può rimuovere contenuti contrari alla legge o ai presenti Termini, anche senza preavviso.</p>

                  <h4 className="text-base font-semibold text-foreground mt-3 mb-2">14.5 Tutela dell'URL e del QR Code</h4>
                  <p className="text-muted-foreground leading-relaxed">L'URL pubblico e il QR code restano di proprietà di Twibi S.r.l. e possono essere modificati o revocati.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">15. Trattamento Dati dell'Operatore</h3>
                  <p className="text-muted-foreground leading-relaxed">Twibi S.r.l. tratta i dati dell'Operatore quale titolare per l'esecuzione del rapporto contrattuale.</p>
                  <p className="text-muted-foreground leading-relaxed">L'Operatore può esercitare i diritti previsti dal GDPR (accesso, cancellazione, portabilità).</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">16. Manleva per Contenuti</h3>
                  <p className="text-muted-foreground leading-relaxed">L'Operatore si impegna a manlevare e tenere indenne Twibi S.r.l. da qualsiasi pretesa derivante da violazioni di diritti di terzi connesse ai contenuti caricati.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">17. Assenza di Esclusiva</h3>
                  <p className="text-muted-foreground leading-relaxed">La presenza sulla piattaforma non comporta esclusiva territoriale o commerciale.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">18. Nessuna Garanzia di Risultati</h3>
                  <p className="text-muted-foreground leading-relaxed">L'utilizzo della piattaforma non garantisce traffico, lead o risultati economici.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">19. Nessun Rapporto di Agenzia</h3>
                  <p className="text-muted-foreground leading-relaxed">Non si configura alcun rapporto di agenzia, partnership o rappresentanza tra le parti.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">20. Sospensione e Revoca</h3>
                  <p className="text-muted-foreground leading-relaxed">Twibi S.r.l. può sospendere o revocare l'account in caso di violazioni.</p>
                  <p className="text-muted-foreground leading-relaxed">Non è previsto rimborso.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">21. Forza Maggiore</h3>
                  <p className="text-muted-foreground leading-relaxed">Twibi S.r.l. non è responsabile per interruzioni dovute a cause di forza maggiore o eventi fuori dal proprio controllo.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">22. Modifiche e Aggiornamenti Tecnici</h3>
                  <p className="text-muted-foreground leading-relaxed">Twibi S.r.l. può modificare funzionalità e algoritmi senza che ciò costituisca inadempimento.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">23. Limitazione di Responsabilità</h3>
                  <p className="text-muted-foreground leading-relaxed">Il servizio è fornito "as is".</p>
                  <p className="text-muted-foreground leading-relaxed">Twibi S.r.l. non è responsabile per perdita di clientela o mancato guadagno.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">24. Legge Applicabile</h3>
                  <p className="text-muted-foreground leading-relaxed">Le presenti Condizioni sono regolate dalla legge italiana.</p>
                  <p className="text-muted-foreground leading-relaxed">Foro competente: Roma.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">Contatti</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Per domande sulle presenti Condizioni: <a href="mailto:info@gildy.it" className="text-gold hover:text-gold/80">info@gildy.it</a>
                  </p>
                </section>
              </div>
            </div>
          </div>
        </div>
        
        <div className="px-6 py-3 border-t bg-muted/30 flex-shrink-0">
          <div className="flex items-center justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              className="gap-2"
            >
              <X className="h-4 w-4" />
              Chiudi
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}